import { useMutation } from '@tanstack/react-query';

import { ApiErrorCode, createApiError } from '@/modules/common/api/errors';
import { useApiError } from '@/modules/common/hooks/use-api-error';

import { getProjectBillingConflicts } from '../../api/projects.api';
import { ConflictResolutionData, ProjectBillingSnapshot } from '../../types/project-conflicts';

interface CheckConflictsParams {
  projectId: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
}

interface CheckConflictsResult {
  items: ProjectBillingSnapshot[];
  hasConflicts: boolean;
}

/**
 * Hook for checking project billing conflicts during form submission.
 * Handles API call and error toasts internally following the mutation pattern.
 *
 * @example
 * ```typescript
 * const { checkConflicts, isPending } = useCheckProjectBillingConflicts();
 *
 * const handleSubmit = async () => {
 *   const result = await checkConflicts({ projectId, effectiveStartDate, effectiveEndDate });
 *   if (result.hasConflicts) {
 *     // Show conflict modal
 *   } else {
 *     // Proceed with submission
 *   }
 * };
 * ```
 */
export const useCheckProjectBillingConflicts = () => {
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const mutation = useMutation({
    mutationFn: async ({
      projectId,
      effectiveStartDate,
      effectiveEndDate,
    }: CheckConflictsParams): Promise<CheckConflictsResult> => {
      const conflictData: ConflictResolutionData = await getProjectBillingConflicts({
        projectId: Number(projectId),
        effectiveStartDate,
        effectiveEndDate,
      });

      return {
        items: conflictData.items as ProjectBillingSnapshot[],
        hasConflicts: conflictData.items.length > 0,
      };
    },
    onError: error => {
      const apiError = createApiError(ApiErrorCode.PROJECT_BILLING_CONFLICTS_LOAD_FAILED, error);
      displayToastErrorFromApiWithFallback(
        apiError,
        'authenticated.projects:api.error.billingConflictsLoadFailed'
      );
    },
  });

  return {
    checkConflicts: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
};
